// ═══════════════════════════════════════════════════════════════════════════
// FARM-BOOKING - FORMULAIRE LIAISON PARCELLE ↔ ESP32 ↔ CULTURE
// ═══════════════════════════════════════════════════════════════════════════
// 73+ Papy - Janvier 2026
// Fichier : frontend/src/components/FormulaireLiaisonCulture.jsx
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { 
    MapPin, Cpu, Leaf, Calendar, Save, X, ChevronRight, 
    CheckCircle, AlertTriangle, Droplets, Thermometer,
    ArrowRight, Clock, Sprout, Info
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const FormulaireLiaisonCulture = ({ onClose, onSuccess }) => {
    // ═══════════════════════════════════════════════════════════════════════
    // ÉTATS
    // ═══════════════════════════════════════════════════════════════════════
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // Données pour les selects
    const [parcelles, setParcelles] = useState([]);
    const [modulesIot, setModulesIot] = useState([]);
    const [familles, setFamilles] = useState([]);
    const [varietes, setVarietes] = useState([]);
    const [stades, setStades] = useState([]);
    
    // Formulaire
    const [form, setForm] = useState({
        parcelle_id: '',
        module_id: '',
        famille_id: '',
        variete_id: '',
        date_plantation: new Date().toISOString().split('T')[0],
        type_plantation: 'semis',
        surface_m2: '',
        nb_plants: '',
        notes: ''
    });
    
    // Données sélectionnées (pour affichage)
    const [selectedParcelle, setSelectedParcelle] = useState(null);
    const [selectedModule, setSelectedModule] = useState(null);
    const [selectedVariete, setSelectedVariete] = useState(null);
    const [calendrierPrevu, setCalendrierPrevu] = useState([]);

    // ═══════════════════════════════════════════════════════════════════════
    // CHARGEMENT INITIAL
    // ═══════════════════════════════════════════════════════════════════════
    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        setLoading(true);
        try {
            const [parcRes, modRes, famRes] = await Promise.all([
                fetch(`${API_BASE}/cultures-gestion/parcelles-select`),
                fetch(`${API_BASE}/cultures-gestion/modules-iot-select`),
                fetch(`${API_BASE}/cultures-gestion/familles`)
            ]);
            
            const parcData = await parcRes.json();
            const modData = await modRes.json();
            const famData = await famRes.json();
            
            if (parcData.success) setParcelles(parcData.data);
            if (modData.success) setModulesIot(modData.data);
            if (famData.success) setFamilles(famData.data);
            
        } catch (err) {
            setError('Erreur chargement des données');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // CHARGEMENT VARIÉTÉS QUAND FAMILLE CHANGE
    // ═══════════════════════════════════════════════════════════════════════
    useEffect(() => {
        if (form.famille_id) {
            loadVarietes(form.famille_id);
        } else {
            setVarietes([]);
        }
    }, [form.famille_id]);

    const loadVarietes = async (familleId) => {
        try {
            const res = await fetch(`${API_BASE}/cultures-gestion/varietes-kc-select?famille_id=${familleId}`);
            const data = await res.json();
            if (data.success) setVarietes(data.data);
        } catch (err) {
            console.error('Erreur chargement variétés:', err);
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // CHARGEMENT STADES QUAND VARIÉTÉ CHANGE
    // ═══════════════════════════════════════════════════════════════════════
    useEffect(() => {
        if (form.variete_id) {
            loadStades(form.variete_id);
            const v = varietes.find(v => v.variete_id == form.variete_id);
            setSelectedVariete(v);
            genererCalendrierPrevu(v);
        } else {
            setStades([]);
            setSelectedVariete(null);
            setCalendrierPrevu([]);
        }
    }, [form.variete_id, form.date_plantation]);

    const loadStades = async (varieteId) => {
        try {
            const res = await fetch(`${API_BASE}/cultures-gestion/stades/${varieteId}`);
            const data = await res.json();
            if (data.success) setStades(data.data);
        } catch (err) {
            console.error('Erreur chargement stades:', err);
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // GÉNÉRATION CALENDRIER PRÉVISIONNEL
    // ═══════════════════════════════════════════════════════════════════════
    const genererCalendrierPrevu = (variete) => {
        if (!variete || !form.date_plantation) return;
        
        const dateDebut = new Date(form.date_plantation);
        const stadesDef = [
            { code: 'INIT', nom: 'Germination/Levée', duree: 20, kc: variete.kc_initial },
            { code: 'DEV', nom: 'Développement', duree: 30, kc: variete.kc_developpement },
            { code: 'MID', nom: 'Mi-saison', duree: 40, kc: variete.kc_mi_saison },
            { code: 'FIN', nom: 'Arrière-saison', duree: 25, kc: variete.kc_arriere_saison }
        ];
        
        let dateCourante = new Date(dateDebut);
        const calendrier = stadesDef.map(stade => {
            const debut = new Date(dateCourante);
            const fin = new Date(dateCourante);
            fin.setDate(fin.getDate() + stade.duree);
            
            const item = {
                ...stade,
                date_debut: debut.toISOString().split('T')[0],
                date_fin: fin.toISOString().split('T')[0]
            };
            
            dateCourante = new Date(fin);
            return item;
        });
        
        setCalendrierPrevu(calendrier);
    };

    // ═══════════════════════════════════════════════════════════════════════
    // HANDLERS
    // ═══════════════════════════════════════════════════════════════════════
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        
        // Mise à jour des sélections pour affichage
        if (name === 'parcelle_id') {
            setSelectedParcelle(parcelles.find(p => p.id == value));
        }
        if (name === 'module_id') {
            setSelectedModule(modulesIot.find(m => m.id == value));
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const res = await fetch(`${API_BASE}/cultures-gestion/creer-complete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    parcelle_id: parseInt(form.parcelle_id),
                    variete_id: parseInt(form.variete_id),
                    date_plantation: form.date_plantation,
                    type_plantation: form.type_plantation,
                    surface_m2: form.surface_m2 ? parseFloat(form.surface_m2) : null,
                    nb_plants: form.nb_plants ? parseInt(form.nb_plants) : null,
                    module_id: form.module_id ? parseInt(form.module_id) : null,
                    notes: form.notes || null
                })
            });
            
            const data = await res.json();
            
            if (data.success) {
                if (onSuccess) onSuccess(data);
                setStep(4); // Étape confirmation
            } else {
                setError(data.error || 'Erreur lors de la création');
            }
        } catch (err) {
            setError('Erreur réseau');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // VALIDATION ÉTAPES
    // ═══════════════════════════════════════════════════════════════════════
    const canGoNext = () => {
        switch (step) {
            case 1: return form.parcelle_id;
            case 2: return form.variete_id && form.date_plantation;
            case 3: return true; // Module optionnel
            default: return false;
        }
    };

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU ÉTAPE 1 : SÉLECTION PARCELLE
    // ═══════════════════════════════════════════════════════════════════════
    const renderStep1 = () => (
        <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
                <MapPin className="w-5 h-5 text-green-600" />
                Étape 1 : Choisir la parcelle
            </h3>
            
            <div className="grid gap-3">
                {parcelles.map(p => (
                    <label 
                        key={p.id}
                        className={`
                            flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all
                            ${form.parcelle_id == p.id 
                                ? 'border-green-500 bg-green-50' 
                                : 'border-gray-200 hover:border-green-300'}
                            ${p.a_culture_active ? 'opacity-60' : ''}
                        `}
                    >
                        <input
                            type="radio"
                            name="parcelle_id"
                            value={p.id}
                            checked={form.parcelle_id == p.id}
                            onChange={handleChange}
                            className="mr-3"
                            disabled={p.a_culture_active}
                        />
                        <div className="flex-1">
                            <div className="font-medium">{p.nom_complet}</div>
                            <div className="text-sm text-gray-500 flex items-center gap-3">
                                <span>{p.surface_m2} m²</span>
                                {p.type_abri !== 'aucun' && (
                                    <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs">
                                        {p.type_abri}
                                    </span>
                                )}
                                {p.a_module_iot ? (
                                    <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs flex items-center gap-1">
                                        <Cpu className="w-3 h-3" /> {p.code_module}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                        {p.a_culture_active ? (
                            <span className="text-orange-600 text-sm flex items-center gap-1">
                                <Leaf className="w-4 h-4" />
                                {p.culture_active_nom}
                            </span>
                        ) : (
                            <span className="text-green-600 text-sm">Disponible</span>
                        )}
                    </label>
                ))}
            </div>
        </div>
    );

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU ÉTAPE 2 : SÉLECTION CULTURE + DATE
    // ═══════════════════════════════════════════════════════════════════════
    const renderStep2 = () => (
        <div className="space-y-6">
            <h3 className="text-lg font-semibold flex items-center gap-2">
                <Leaf className="w-5 h-5 text-green-600" />
                Étape 2 : Choisir la culture
            </h3>
            
            {/* Famille */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Famille botanique
                </label>
                <select
                    name="famille_id"
                    value={form.famille_id}
                    onChange={handleChange}
                    className="w-full border rounded-lg p-3 focus:ring-2 focus:ring-green-500"
                >
                    <option value="">-- Sélectionner une famille --</option>
                    {familles.map(f => (
                        <option key={f.id} value={f.id}>
                            {f.icone} {f.nom}
                        </option>
                    ))}
                </select>
            </div>
            
            {/* Variété */}
            {form.famille_id && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Variété
                    </label>
                    <select
                        name="variete_id"
                        value={form.variete_id}
                        onChange={handleChange}
                        className="w-full border rounded-lg p-3 focus:ring-2 focus:ring-green-500"
                    >
                        <option value="">-- Sélectionner une variété --</option>
                        {varietes.map(v => (
                            <option key={v.variete_id} value={v.variete_id}>
                                {v.icone} {v.nom_complet}
                            </option>
                        ))}
                    </select>
                </div>
            )}
            
            {/* Coefficients Kc si variété sélectionnée */}
            {selectedVariete && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
                        <Droplets className="w-4 h-4" />
                        Coefficients Kc FAO-56
                    </h4>
                    <div className="grid grid-cols-4 gap-2 text-center">
                        <div className="bg-white rounded p-2">
                            <div className="text-xs text-gray-500">INIT</div>
                            <div className="text-lg font-bold text-blue-600">{selectedVariete.kc_initial}</div>
                        </div>
                        <div className="bg-white rounded p-2">
                            <div className="text-xs text-gray-500">DEV</div>
                            <div className="text-lg font-bold text-blue-600">{selectedVariete.kc_developpement}</div>
                        </div>
                        <div className="bg-white rounded p-2">
                            <div className="text-xs text-gray-500">MID</div>
                            <div className="text-lg font-bold text-blue-600">{selectedVariete.kc_mi_saison}</div>
                        </div>
                        <div className="bg-white rounded p-2">
                            <div className="text-xs text-gray-500">FIN</div>
                            <div className="text-lg font-bold text-blue-600">{selectedVariete.kc_arriere_saison}</div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Date et type de plantation */}
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        Date de plantation
                    </label>
                    <input
                        type="date"
                        name="date_plantation"
                        value={form.date_plantation}
                        onChange={handleChange}
                        className="w-full border rounded-lg p-3 focus:ring-2 focus:ring-green-500"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Type
                    </label>
                    <select
                        name="type_plantation"
                        value={form.type_plantation}
                        onChange={handleChange}
                        className="w-full border rounded-lg p-3 focus:ring-2 focus:ring-green-500"
                    >
                        <option value="semis">🌱 Semis direct</option>
                        <option value="repiquage">🌿 Repiquage</option>
                    </select>
                </div>
            </div>
            
            {/* Surface et plants */}
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Surface (m²)
                    </label>
                    <input
                        type="number"
                        name="surface_m2"
                        value={form.surface_m2}
                        onChange={handleChange}
                        placeholder={selectedParcelle?.surface_m2 || ''}
                        className="w-full border rounded-lg p-3 focus:ring-2 focus:ring-green-500"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre de plants
                    </label>
                    <input
                        type="number"
                        name="nb_plants"
                        value={form.nb_plants}
                        onChange={handleChange}
                        className="w-full border rounded-lg p-3 focus:ring-2 focus:ring-green-500"
                    />
                </div>
            </div>
            
            {/* Calendrier prévisionnel */}
            {calendrierPrevu.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Calendrier prévisionnel
                    </h4>
                    <div className="space-y-2">
                        {calendrierPrevu.map((stade, idx) => (
                            <div key={idx} className="flex items-center text-sm">
                                <span className="w-16 font-mono text-green-700">{stade.code}</span>
                                <span className="flex-1">{stade.nom}</span>
                                <span className="text-gray-500">
                                    {new Date(stade.date_debut).toLocaleDateString('fr-FR')} → {new Date(stade.date_fin).toLocaleDateString('fr-FR')}
                                </span>
                                <span className="ml-3 text-blue-600 font-medium">Kc {stade.kc}</span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-green-200 text-sm text-green-700">
                        📅 Récolte estimée : <strong>{calendrierPrevu[calendrierPrevu.length - 1]?.date_fin}</strong>
                    </div>
                </div>
            )}
        </div>
    );

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU ÉTAPE 3 : MODULE IoT (OPTIONNEL)
    // ═══════════════════════════════════════════════════════════════════════
    const renderStep3 = () => (
        <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
                <Cpu className="w-5 h-5 text-green-600" />
                Étape 3 : Module IoT (optionnel)
            </h3>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                <Info className="w-4 h-4 inline mr-2" />
                L'association d'un module ESP32 permet le suivi automatique des conditions de culture.
            </div>
            
            <div className="grid gap-3">
                <label 
                    className={`
                        flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all
                        ${!form.module_id 
                            ? 'border-gray-400 bg-gray-50' 
                            : 'border-gray-200 hover:border-gray-300'}
                    `}
                >
                    <input
                        type="radio"
                        name="module_id"
                        value=""
                        checked={!form.module_id}
                        onChange={handleChange}
                        className="mr-3"
                    />
                    <div className="flex-1">
                        <div className="font-medium">Aucun module IoT</div>
                        <div className="text-sm text-gray-500">Suivi manuel uniquement</div>
                    </div>
                </label>
                
                {modulesIot.filter(m => m.disponibilite === 'disponible').map(m => (
                    <label 
                        key={m.id}
                        className={`
                            flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all
                            ${form.module_id == m.id 
                                ? 'border-green-500 bg-green-50' 
                                : 'border-gray-200 hover:border-green-300'}
                        `}
                    >
                        <input
                            type="radio"
                            name="module_id"
                            value={m.id}
                            checked={form.module_id == m.id}
                            onChange={handleChange}
                            className="mr-3"
                        />
                        <div className="flex-1">
                            <div className="font-medium flex items-center gap-2">
                                <Cpu className="w-4 h-4" />
                                {m.code_module}
                            </div>
                            <div className="text-sm text-gray-500">{m.nom}</div>
                        </div>
                        <span className={`text-sm ${m.statut === 'online' ? 'text-green-600' : 'text-gray-500'}`}>
                            {m.statut_affichage}
                        </span>
                    </label>
                ))}
            </div>
            
            {/* Notes */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes (optionnel)
                </label>
                <textarea
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Observations, particularités..."
                    className="w-full border rounded-lg p-3 focus:ring-2 focus:ring-green-500"
                />
            </div>
        </div>
    );

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU ÉTAPE 4 : CONFIRMATION
    // ═══════════════════════════════════════════════════════════════════════
    const renderStep4 = () => (
        <div className="text-center py-8">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-green-800 mb-2">
                Culture créée avec succès !
            </h3>
            <p className="text-gray-600 mb-6">
                La liaison Parcelle ↔ Culture {form.module_id ? '↔ ESP32' : ''} est établie.
            </p>
            
            <div className="bg-gray-50 rounded-lg p-4 text-left max-w-md mx-auto">
                <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                        <span className="text-gray-500">Parcelle :</span>
                        <span className="font-medium">{selectedParcelle?.nom_complet}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-500">Culture :</span>
                        <span className="font-medium">{selectedVariete?.nom_complet}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-gray-500">Date plantation :</span>
                        <span className="font-medium">{new Date(form.date_plantation).toLocaleDateString('fr-FR')}</span>
                    </div>
                    {selectedModule && (
                        <div className="flex justify-between">
                            <span className="text-gray-500">Module IoT :</span>
                            <span className="font-medium">{selectedModule.code_module}</span>
                        </div>
                    )}
                </div>
            </div>
            
            <button
                onClick={onClose}
                className="mt-6 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
            >
                Fermer
            </button>
        </div>
    );

    // ═══════════════════════════════════════════════════════════════════════
    // RENDU PRINCIPAL
    // ═══════════════════════════════════════════════════════════════════════
    return (
        <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-4 flex justify-between items-center">
                <h2 className="text-xl font-bold flex items-center gap-2">
                    <Sprout className="w-6 h-6" />
                    Nouvelle liaison Parcelle ↔ Culture
                </h2>
                <button onClick={onClose} className="p-1 hover:bg-white/20 rounded">
                    <X className="w-6 h-6" />
                </button>
            </div>
            
            {/* Progress */}
            {step < 4 && (
                <div className="px-6 pt-4">
                    <div className="flex items-center justify-between mb-2">
                        {[1, 2, 3].map(s => (
                            <React.Fragment key={s}>
                                <div className={`
                                    w-10 h-10 rounded-full flex items-center justify-center font-bold
                                    ${s < step ? 'bg-green-600 text-white' : 
                                      s === step ? 'bg-green-100 text-green-700 ring-2 ring-green-600' : 
                                      'bg-gray-100 text-gray-400'}
                                `}>
                                    {s < step ? <CheckCircle className="w-5 h-5" /> : s}
                                </div>
                                {s < 3 && (
                                    <div className={`flex-1 h-1 mx-2 ${s < step ? 'bg-green-600' : 'bg-gray-200'}`} />
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 px-2">
                        <span>Parcelle</span>
                        <span>Culture</span>
                        <span>IoT</span>
                    </div>
                </div>
            )}
            
            {/* Contenu */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
                {loading && step === 1 ? (
                    <div className="text-center py-8">
                        <div className="animate-spin w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full mx-auto mb-4" />
                        <p>Chargement...</p>
                    </div>
                ) : (
                    <>
                        {step === 1 && renderStep1()}
                        {step === 2 && renderStep2()}
                        {step === 3 && renderStep3()}
                        {step === 4 && renderStep4()}
                    </>
                )}
                
                {error && (
                    <div className="mt-4 bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" />
                        {error}
                    </div>
                )}
            </div>
            
            {/* Footer */}
            {step < 4 && (
                <div className="border-t p-4 flex justify-between">
                    <button
                        onClick={() => setStep(s => Math.max(1, s - 1))}
                        disabled={step === 1}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                    >
                        ← Retour
                    </button>
                    
                    {step < 3 ? (
                        <button
                            onClick={() => setStep(s => s + 1)}
                            disabled={!canGoNext()}
                            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            Suivant <ChevronRight className="w-4 h-4" />
                        </button>
                    ) : (
                        <button
                            onClick={handleSubmit}
                            disabled={loading}
                            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                    Création...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" /> Créer la culture
                                </>
                            )}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default FormulaireLiaisonCulture;